'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import * as RechartsPrimitive from 'recharts';
import {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';

import { useBudgetStatus } from '@/features/charts/hooks/use-budget-status';
import { useKpiTranslations } from '@/features/projects/project-details/components/kpi-card';
import AddFileIcon from '@/shared/assets/add_file_icon.svg?react';
import AnomalyIcon from '@/shared/assets/anomaly_icon.png';
import ArrowDownIcon from '@/shared/assets/arrow-down.svg?react';
import ArrowRightIcon from '@/shared/assets/arrow-right.svg?react';
import ArrowUpIcon from '@/shared/assets/arrow-up.svg?react';
import BudgetIcon from '@/shared/assets/budget.svg?react';
import CalendarIcon from '@/shared/assets/calendar.svg?react';
import GrowthRateIcon from '@/shared/assets/growth_rate.svg?react';
import HandIcon from '@/shared/assets/hand.png';
import InfoIcon from '@/shared/assets/info.svg?react';
import PersonWhiteIcon from '@/shared/assets/person_white.png';
import WarningMarkIcon from '@/shared/assets/warning_mark.svg?react';
import { Gradients } from '@/shared/components/theme/gradient';
import RoundedIcon from '@/shared/components/ui/rounded-icon';
import { getKpiUnitAbbreviationTranslation } from '@/shared/hooks/use-kpi-unit-translation';
import { cn } from '@/shared/utils/cn';
import { formatDate, formatNumberWithDots } from '@/shared/utils/format';

export const ChartTooltipContentSingle = (
  props: RechartsPrimitive.TooltipProps<ValueType, NameType>,
) => {
  const { t } = useTranslation('chart', { keyPrefix: 'tooltip' });
  const { t: tInsights } = useTranslation('insight', {
    keyPrefix: 'InsightsHub.Anomalies',
  });
  const { tKpiTypes } = useKpiTranslations();
  const unit = props.payload?.[0]?.payload.unit;
  const unitAbbreviation = getKpiUnitAbbreviationTranslation(tKpiTypes, unit);

  return (
    <div className="rounded-md border-[0.5px] border-white/10 bg-dropdown-gray backdrop-blur-[30px]">
      <div
        className={cn(
          'flex flex-col border-white/15 bg-gradient-to-br',
          Gradients.dropdownGradient,
        )}
      >
        {props.payload
          ?.filter((item) => item.value !== null && item.value !== undefined)
          .filter((item, index, array) => {
            const dataKey = String(item.dataKey);
            if (dataKey.endsWith('_forecast')) {
              const baseKey = dataKey.replace('_forecast', '');
              const hasActual = array.some(
                (other) => String(other.dataKey) === baseKey,
              );
              return !hasActual;
            }
            return true;
          })
          .map((item) => {
            const isForecast = item.payload.isForecast;
            const displayValue = isForecast
              ? Number(item.payload.forecastValue)
              : Number(item.value);
            var value = displayValue;
            var date = item.payload.date;
            if (date) {
              date = formatDate({
                date: date,
                formatType: 'digits',
              });
            }

            var budget = item.payload.budget
              ? Number(item.payload.budget)
              : null;
            const exceededBudget =
              budget !== null && !isNaN(value) && value > budget;
            const lastEntryValue = item.payload.lastEntryValue;
            const growthRate =
              lastEntryValue && value && lastEntryValue !== 0
                ? ((Number(value) - Number(lastEntryValue)) /
                    Number(lastEntryValue)) *
                  100
                : null;
            const isAnomaly = item.payload.isAnomaly;
            const deviationPercentage = item.payload.deviationPercentage;
            const forecastConfidence = item.payload.forecastConfidence;
            const forecastUpperBound = item.payload.forecastUpperBound;
            const forecastLowerBound = item.payload.forecastLowerBound;
            const partnerName = item.payload.partnerName;

            // Calculate trend direction for forecast arrow
            let forecastTrend: 'up' | 'down' | 'neutral' = 'neutral';
            if (
              isForecast &&
              displayValue !== null &&
              lastEntryValue !== null
            ) {
              if (displayValue > lastEntryValue) {
                forecastTrend = 'up';
              } else if (displayValue < lastEntryValue) {
                forecastTrend = 'down';
              }
            }

            // Determine which arrow to show for the main forecast value
            const ForecastArrowIcon =
              forecastTrend === 'up'
                ? ArrowUpIcon
                : forecastTrend === 'down'
                  ? ArrowDownIcon
                  : ArrowRightIcon;

            return (
              <div key={item.dataKey} className="w-72">
                <div className="flex flex-row justify-between pl-[14px] pr-2 pt-2">
                  <span className="text-2xs">
                    {isAnomaly ? t('title_anomaly') : t('title')}
                  </span>
                  <div className="flex items-center gap-2">
                    {isAnomaly ? (
                      <img src={AnomalyIcon} alt="Anomaly" className="size-5" />
                    ) : (
                      budget && (
                        <ToolTipBudgetStatus
                          value={Number(value)}
                          budget={budget}
                          exceededBudget={exceededBudget}
                        />
                      )
                    )}
                  </div>
                </div>
                <div className="my-2 h-px w-full bg-white/30" />

                <div
                  className="flex flex-col items-start gap-1 px-2 pb-2"
                  key={item.dataKey}
                >
                  {isAnomaly &&
                    deviationPercentage !== null &&
                    deviationPercentage !== undefined && (
                      <ToolTipInformationItem
                        icon={
                          <div className="flex size-5 items-center justify-center">
                            <img
                              src={HandIcon}
                              alt="Hand"
                              className="size-4 object-contain"
                            />
                          </div>
                        }
                        value={
                          <span>
                            {deviationPercentage > 0 ? '+' : ''}
                            {deviationPercentage.toFixed(1)}%
                          </span>
                        }
                        info={tInsights('deviation')}
                      />
                    )}
                  {partnerName && (
                    <ToolTipInformationItem
                      icon={
                        <div className="flex size-5 items-center justify-center">
                          <img
                            src={PersonWhiteIcon}
                            alt="Person"
                            className="size-4"
                          />
                        </div>
                      }
                      value={partnerName}
                      info={(t('partner' as const) as string) || 'Partner'}
                    />
                  )}
                  {date && (
                    <ToolTipInformationItem
                      icon={
                        <CalendarIcon className="size-5 text-primary-shade-grey-500" />
                      }
                      value={date}
                      info={t('date')}
                    />
                  )}
                  {!isAnomaly && (
                    <>
                      <ToolTipInformationItem
                        icon={
                          isForecast ? (
                            <ForecastArrowIcon className="size-5 text-primary-shade-grey-500" />
                          ) : (
                            <AddFileIcon className="size-5 text-primary-shade-grey-500" />
                          )
                        }
                        value={
                          unit
                            ? `${formatNumberWithDots(value)} ${unitAbbreviation}`
                            : formatNumberWithDots(value)
                        }
                        info={
                          isForecast
                            ? (t('forecast_value' as const) as string)
                            : t('value')
                        }
                        isHighlighted
                      />
                      {growthRate && (
                        <ToolTipInformationItem
                          icon={
                            <GrowthRateIcon className="size-5 text-primary-shade-grey-500" />
                          }
                          value={`${growthRate > 0 ? '+' : ''}${growthRate.toFixed(1)} %`}
                          info={t('growth_rate')}
                        />
                      )}
                      {budget && (
                        <ToolTipInformationItem
                          icon={
                            <BudgetIcon className="size-5 text-primary-shade-grey-500" />
                          }
                          value={
                            unit
                              ? `${formatNumberWithDots(budget)} ${unitAbbreviation}`
                              : formatNumberWithDots(budget)
                          }
                          info={t('budget')}
                          isWarning={exceededBudget}
                        />
                      )}
                      {isForecast &&
                        forecastConfidence !== null &&
                        forecastConfidence !== undefined && (
                          <ToolTipInformationItem
                            icon={
                              <InfoIcon className="size-5 text-primary-shade-grey-500" />
                            }
                            value={`${forecastConfidence.toFixed(1)}%`}
                            info={t('confidence' as const) as string}
                          />
                        )}
                      {isForecast &&
                        forecastUpperBound !== null &&
                        forecastUpperBound !== undefined && (
                          <ToolTipInformationItem
                            icon={
                              <ArrowUpIcon className="size-5 text-primary-shade-grey-500" />
                            }
                            value={
                              unit
                                ? `${formatNumberWithDots(forecastUpperBound)} ${unitAbbreviation}`
                                : formatNumberWithDots(forecastUpperBound)
                            }
                            info={t('upper_bound' as const) as string}
                          />
                        )}
                      {isForecast &&
                        forecastLowerBound !== null &&
                        forecastLowerBound !== undefined && (
                          <ToolTipInformationItem
                            icon={
                              <ArrowDownIcon className="size-5 text-primary-shade-grey-500" />
                            }
                            value={
                              unit
                                ? `${formatNumberWithDots(forecastLowerBound)} ${unitAbbreviation}`
                                : formatNumberWithDots(forecastLowerBound)
                            }
                            info={t('lower_bound' as const) as string}
                          />
                        )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

const ToolTipBudgetStatus = ({
  value,
  budget,
  exceededBudget,
}: {
  value: number;
  budget: number;
  exceededBudget: boolean;
}) => {
  const { gradient } = useBudgetStatus(value, budget);

  return (
    <RoundedIcon
      bgColor={gradient}
      className="!size-[18px]"
      icon={
        exceededBudget ? (
          <WarningMarkIcon className="size-[10px]" />
        ) : (
          <ArrowRightIcon className="size-[10px]" />
        )
      }
    />
  );
};

const ToolTipInformationItem = ({
  icon,
  value,
  info,
  isHighlighted,
  isWarning,
}: {
  icon: React.ReactNode;
  value: ValueType | React.ReactNode | undefined;
  info: string;
  isHighlighted?: boolean;
  isWarning?: boolean;
}) => {
  return (
    <div className="flex w-full flex-row items-center justify-between p-2">
      <div className="flex flex-row items-center gap-1">
        {icon}
        <span className="flex-shrink-0 text-2xs text-primary-shade-grey-500">
          {info}
        </span>
      </div>
      <div
        className={cn(
          'ml-auto flex-shrink-0',
          (isHighlighted || isWarning) &&
            'rounded-full border-[0.5px] border-white/30 bg-primary-shade-grey-500 px-2 py-[2px] !text-3xs font-[700] text-background',
        )}
      >
        <span
          className={cn(
            'text-2xs',
            isWarning &&
              'bg-gradient-to-br from-custom-pink to-custom-red bg-clip-text text-transparent',
          )}
        >
          {value}
        </span>
      </div>
    </div>
  );
};
